import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Activity, Snowflake, Flame, BellRing } from "lucide-react";

const STYLE = {
  "Very responsive": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Responsive": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Engaged": "bg-sky-50 text-sky-700 border-sky-200",
  "Slow to respond": "bg-amber-50 text-amber-700 border-amber-200",
  "Rarely responds": "bg-rose-50 text-rose-700 border-rose-200",
  "No replies yet": "bg-rose-50 text-rose-700 border-rose-200",
  "Gone quiet": "bg-slate-100 text-slate-500 border-slate-200",
};

// Labels we don't clutter the board with (no signal worth a chip)
const HIDDEN = new Set(["No email yet", "Inbound only"]);

export function EngagementChip({ label, replyRate, testid }) {
  if (!label || HIDDEN.has(label)) return null;
  const cls = STYLE[label] || "bg-slate-50 text-slate-600 border-slate-200";
  const title = replyRate != null ? `${label} · replies to ${replyRate}% of follow-ups` : label;
  return (
    <span data-testid={testid} title={title}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${cls}`}>
      <Activity className="w-2.5 h-2.5" /> {label}
    </span>
  );
}

// Project-level engagement state (from the engagement sweep). Distinct from the
// client-responsiveness chip above: this is about THIS pursuit's momentum.
const STATE = {
  owed_reply: { cls: "bg-[#F4C400]/15 text-[#8a6d00] border-[#F4C400]/40", Icon: BellRing, label: "You owe a reply" },
  chasing: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Flame, label: "Worth chasing" },
  quiet: { cls: "bg-slate-100 text-slate-600 border-slate-300", Icon: Snowflake, label: "Gone quiet" },
  cold: { cls: "bg-slate-200 text-slate-700 border-slate-300", Icon: Snowflake, label: "Parked cold" },
};

export function EngagementStateChip({ state, silenceDays, followups, testid }) {
  const s = STATE[state];
  if (!s) return null; // active / unknown → no chip
  const { Icon } = s;
  const bits = [s.label];
  if ((state === "quiet" || state === "cold") && silenceDays != null) bits.push(`silent ${silenceDays}d`);
  if ((state === "quiet" || state === "cold") && followups) bits.push(`${followups} follow-up${followups === 1 ? "" : "s"} unanswered`);
  const text = bits.join(" · ");
  return (
    <span data-testid={testid} title={text}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${s.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {text}
    </span>
  );
}

export function AssigneeAvatar({ assignee, size = "w-6 h-6", testid }) {
  if (!assignee) return null;
  return (
    <Avatar className={`${size} shrink-0 ring-1 ring-white`} title={assignee.name} data-testid={testid}>
      {assignee.picture && <AvatarImage src={assignee.picture} alt={assignee.name} />}
      <AvatarFallback className="bg-[#003E87] text-white text-[9px] font-semibold">{assignee.initials || "?"}</AvatarFallback>
    </Avatar>
  );
}
